// Connecting-shape resolution (ENG-13, M10.2). PURE: computes the n/e/s/w
// connection booleans for a fence / wall / pane from its neighbours, so the host
// can update the block state on placement and on any neighbour change. Fences
// connect to fences, fence gates, and solid cubes; walls to walls + solids; panes
// to panes, glass, and solids.
import { SHAPE } from '../shapes';

export interface ConnectAccess {
  /** Shape id at (x,y,z). */
  shapeAt(x: number, y: number, z: number): number;
  /** A full opaque cube (fences/walls/panes anchor to these). */
  isSolidCube(x: number, y: number, z: number): boolean;
  /** Glass-like (panes also connect to glass blocks + other panes). */
  isGlassLike(x: number, y: number, z: number): boolean;
}

export interface Sides {
  north: boolean;
  east: boolean;
  south: boolean;
  west: boolean;
}

/** True if a connecting block of `shape` links to the block at (x,y,z). */
function links(a: ConnectAccess, shape: number, x: number, y: number, z: number): boolean {
  if (a.isSolidCube(x, y, z)) return true;
  const s = a.shapeAt(x, y, z);
  if (shape === SHAPE.FENCE) return s === SHAPE.FENCE || s === SHAPE.FENCE_GATE;
  if (shape === SHAPE.WALL) return s === SHAPE.WALL;
  if (shape === SHAPE.PANE) return s === SHAPE.PANE || a.isGlassLike(x, y, z);
  return false;
}

/** The n/e/s/w connections for a connecting block at (x,y,z). */
export function connectSides(a: ConnectAccess, shape: number, x: number, y: number, z: number): Sides {
  return {
    north: links(a, shape, x, y, z - 1),
    south: links(a, shape, x, y, z + 1),
    west: links(a, shape, x - 1, y, z),
    east: links(a, shape, x + 1, y, z),
  };
}

/** Shapes whose state carries n/e/s/w connection props (need re-resolution). */
export function isConnectingShape(shape: number): boolean {
  return shape === SHAPE.FENCE || shape === SHAPE.WALL || shape === SHAPE.PANE;
}
